/**
 * Given a file, produces a data URL.
 *
 * @param file File.
 * @returns Base64 data URL.
 */
export function toDataURL(file: File): Promise<string> {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    fileReader.onload = () => {
      resolve(typeof fileReader.result === "string" ? fileReader.result : "");
    };
    fileReader.readAsDataURL(file);
  });
}

/**
 * Given a blob, produces a file.
 *
 * @param blob Blob object.
 * @param name File name.
 * @param type File MIME type.
 * @returns File.
 */
export function toFile(blob: Blob, name: string, type: string): File {
  return new File([blob], name, {
    type,
    lastModified: Date.now(),
  });
}

/**
 * Resizes given image.
 * ASSUME: given image is square-shaped.
 *
 * @param file Source image file.
 * @param size Width = height.
 * @returns Resized file.
 */
export async function resizeSquareImage(file: File, size = 200): Promise<File> {
  const src = await toDataURL(file);
  return new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      if (image.naturalWidth <= size) {
        resolve(file);
        return;
      }
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.imageSmoothingQuality = "high";
        canvas.width = size;
        canvas.height = size;
        ctx.drawImage(image, 0, 0, size, size);
      }
      canvas.toBlob((blob) => {
        if (!blob) {
          throw new Error("Image resize failed.");
        }
        const fileResized = toFile(blob, file.name, file.type);
        resolve(fileResized);
      }, file.type);
    };
    image.src = src;
  });
}
